import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Flex } from 'antd'
import { useTheme } from 'antd-style'
import { useTranslation } from 'react-i18next'

import { Text } from 'src/components'

import { getPasswordStrength } from '../constants'
import { usePasswordValidationRules } from '../hooks'

import { useStyles } from './styles'

type Props = {
  value: string
}

export const PasswordStrength = ({ value }: Props) => {
  const theme = useTheme()

  const { t } = useTranslation()
  const { styles } = useStyles()

  const { regexArray } = usePasswordValidationRules()

  const passwordStrength = getPasswordStrength(
    regexArray.map((item) => item.regex),
    value
  )

  const passwordStrengthsText = {
    weak: t('auth.passwordRegex.weak'),
    medium: t('auth.passwordRegex.medium'),
    strong: t('auth.passwordRegex.strong')
  }

  const countMatches = (value: string) => {
    return regexArray.reduce(
      (acc, { regex }) => acc + (regex?.exec(value) ? 1 : 0),
      0
    )
  }

  const matches = countMatches(value)

  return (
    <Flex vertical gap={8}>
      <Flex gap={8}>
        {regexArray.map(({ text }, index) => (
          <div
            className={styles.progress}
            key={text}
            style={{
              backgroundColor:
                index < matches ? theme.colorPrimary : theme.grey.grey3
            }}
          />
        ))}
      </Flex>

      <Flex vertical gap={4}>
        <Flex gap={4}>
          <Text>{t('auth.passwordRegex.level')}: </Text>
          <Text weight={600}>{passwordStrengthsText[passwordStrength]}</Text>
        </Flex>

        <Text weight={600}>{t('auth.passwordRegex.levelReq')}: </Text>
      </Flex>

      <Flex gap={4} vertical>
        {regexArray.map(({ regex, text }) => {
          const matchesRegex = regex?.exec(value)

          const matchesRegexColor = matchesRegex
            ? theme.colorPrimary
            : theme.grey.grey3

          return (
            <Flex align="center" key={text}>
              {matchesRegex ? (
                <CheckOutlined
                  style={{
                    color: matchesRegexColor
                  }}
                />
              ) : (
                <CloseOutlined
                  style={{
                    color: theme.grey.grey3
                  }}
                />
              )}

              <Text
                style={{
                  color: matchesRegexColor
                }}
              >
                {text}
              </Text>
            </Flex>
          )
        })}
      </Flex>
    </Flex>
  )
}
